import type { Node, NodeChange } from "@xyflow/react";

type MaybeLocked = { type?: string; locked?: unknown };

/** Only `locked === true` locks; absent or false is unlocked. */
export function isNodeLocked(node: MaybeLocked | undefined | null): boolean {
  return node?.locked === true;
}

const UNLOCKABLE_NODE_TYPES = new Set([
  "section",
  "chatSession",
  "commentMarker",
]);

export function isLockableNode(node: MaybeLocked | undefined | null): boolean {
  return !!node && !UNLOCKABLE_NODE_TYPES.has(node.type ?? "");
}

/**
 * React Flow's own guards for a locked node: no drag (single or group) and no
 * delete. Render-time only — never write these back to storage.
 */
export function applyNodeLocks<T extends Node>(nodes: T[]): T[] {
  return nodes.map((n) =>
    isNodeLocked(n) ? { ...n, draggable: false, deletable: false } : n,
  );
}

/**
 * Drop the render-time flags from `applyNodeLocks` off whole-node changes
 * before they reach storage: `setNodes` diffs against the rendered nodes and
 * emits them as `replace`, which would persist them past an unlock.
 */
export function stripNodeLockFlags<T extends Node>(
  changes: NodeChange<T>[],
): NodeChange<T>[] {
  return changes.map((c) => {
    if ((c.type !== "replace" && c.type !== "add") || !isNodeLocked(c.item)) {
      return c;
    }
    const { draggable: _d, deletable: _del, ...item } = c.item;
    return { ...c, item: item as T };
  });
}

/** A copy starts unlocked. */
export function withoutLock<T extends MaybeLocked>(node: T): T {
  if (!isNodeLocked(node)) return node;
  const {
    locked: _l,
    draggable: _d,
    deletable: _del,
    ...rest
  } = node as T & { draggable?: boolean; deletable?: boolean };
  return rest as T;
}
